import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, Button, useTheme, useMediaQuery } from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
} from '@mui/icons-material';
import { fadeInUp } from './glassMorphismStyles';

interface GlassImageUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
}

const GlassImageUpload: React.FC<GlassImageUploadProps> = ({ value, onChange }) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    if (value) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(value);
    } else {
      setPreview(null);
    }
  }, [value]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        onChange(file);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onChange(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        style={{ display: 'none' }}
        id="image-upload-input"
      />
      
      {!value ? (
        isMobile ? (
          // Mobile: Simple button
          <label htmlFor="image-upload-input">
            <Button
              component="span"
              variant="outlined"
              startIcon={<AddPhotoAlternateIcon />}
              sx={{
                width: '100%',
                py: 1,
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(10, 124, 160, 0.3)',
                backgroundColor: 'rgba(0, 51, 78, 0.2)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 51, 78, 0.3)',
                  borderColor: 'rgba(10, 124, 160, 0.5)',
                },
              }}
            >
              Add Image (optional)
            </Button>
          </label>
        ) : (
          // Desktop: Drag and drop area
          <label htmlFor="image-upload-input">
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${dragActive ? '#0a7ca0' : 'rgba(10, 124, 160, 0.3)'}`,
                borderRadius: 2,
                padding: 3,
                textAlign: 'center',
                cursor: 'pointer',
                background: dragActive 
                  ? 'rgba(10, 124, 160, 0.1)' 
                  : 'rgba(0, 51, 78, 0.2)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                animation: `${fadeInUp} 0.4s ease-out`,
                '&:hover': {
                  background: 'rgba(0, 51, 78, 0.3)',
                  borderColor: 'rgba(10, 124, 160, 0.5)',
                },
              }}
            >
              <CloudUploadIcon 
                sx={{ 
                  fontSize: 48,
                  color: 'rgba(10, 124, 160, 0.7)',
                  mb: 1,
                }} 
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 0.5,
                }}
              >
                Drop image here or click to browse
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                Supports: JPG, PNG, GIF (Max 10MB)
              </Typography>
            </Box>
          </label>
        )
      ) : (
        isMobile ? (
          // Mobile: Show button with image selected state
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              sx={{
                flex: 1,
                py: 1,
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(10, 124, 160, 0.5)',
                backgroundColor: 'rgba(10, 124, 160, 0.2)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: 'rgba(10, 124, 160, 0.3)',
                },
              }}
              onClick={() => inputRef.current?.click()}
            >
              {value.name.length > 20 ? `${value.name.substring(0, 20)}...` : value.name}
            </Button>
            <IconButton
              onClick={handleRemove}
              size="small"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          // Desktop: Show preview
          <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            background: 'rgba(0, 51, 78, 0.3)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(10, 124, 160, 0.3)',
            animation: `${fadeInUp} 0.4s ease-out`,
          }}
        >
          {preview ? (
            <Box
              component="img"
              src={preview}
              alt="Upload preview"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: { xs: 120, sm: 200 },
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                height: { xs: 80, sm: 120 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <ImageIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'rgba(10, 124, 160, 0.5)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {value.name}
              </Typography>
            </Box>
          )}
          
          <IconButton
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        )
      )}
      
      {value && !isMobile && (
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            mt: 1,
            color: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          Image selected: {value.name}
        </Typography>
      )}
    </Box>
  );
};

export default GlassImageUpload;